/**
 * The time maths of `countdown/timer`, pure, so the parser, the server's render, the island and the tests
 * share one copy. A moment is milliseconds since the epoch; an offset is minutes east of UTC.
 */

/** An ISO 8601 date and time with its offset: `2026-11-30T23:59:00+03:00`; seconds and a fraction optional, `Z` for UTC. */
const ISO_WITH_OFFSET = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,9})?)?(?:Z|([+-])(\d{2}):(\d{2}))$/

/**
 * The moment `value` names and the offset it was written in, or null when it is not one: no offset (a
 * different moment on the server and in every browser), a day the month lacks, an hour past 23, an offset
 * beyond ±14:00. A fraction of a second is dropped.
 */
export function readInstant(value: string): { at: number; offset: number } | null {
  const match = ISO_WITH_OFFSET.exec(value)
  if (!match) return null
  const [, y, mo, d, h, mi, s, sign, oh, om] = match
  const [year, month, day, hour, minute] = [y, mo, d, h, mi].map(Number)
  const second = s === undefined ? 0 : Number(s)
  if (month < 1 || month > 12 || day < 1 || hour > 23 || minute > 59 || second > 59) return null
  // Date.UTC rolls 30 February over to 2 March; the day it lands on tells.
  if (new Date(Date.UTC(year, month - 1, day)).getUTCDate() !== day) return null
  const offset = sign ? (sign === "-" ? -1 : 1) * (Number(oh) * 60 + Number(om)) : 0
  if ((sign && Number(om) > 59) || Math.abs(offset) > 14 * 60) return null
  return { at: Date.UTC(year, month - 1, day, hour, minute, second) - offset * 60_000, offset }
}

export type Remaining = { days: number; hours: number; minutes: number; seconds: number }

/**
 * What is left from `now` to `at`, rounded up to the whole second, or null once nothing is: the clock never
 * reads all zeros while the sale is on, and the second it would is the moment the after-end state comes.
 */
export function remaining(at: number, now: number): Remaining | null {
  const left = Math.ceil((at - now) / 1000)
  if (!(left > 0)) return null
  return {
    days: Math.floor(left / 86_400),
    hours: Math.floor(left / 3_600) % 24,
    minutes: Math.floor(left / 60) % 60,
    seconds: left % 60,
  }
}

/** Milliseconds from `now` until the whole seconds left to `at` change: when the island ticks next. */
export function untilNextTick(at: number, now: number): number {
  const part = (at - now) % 1000
  return part > 0 ? part : 1000
}

/** `UTC+3`, `UTC−3:30`, `UTC`: an offset as a shopper reads it. */
export function offsetLabel(offset: number): string {
  if (offset === 0) return "UTC"
  const minutes = Math.abs(offset) % 60
  return `UTC${offset > 0 ? "+" : "−"}${Math.floor(Math.abs(offset) / 60)}${minutes ? `:${String(minutes).padStart(2, "0")}` : ""}`
}

/**
 * The end as the template wrote it, in the visitor's language: the date and time in `endsAt`'s own offset,
 * and the offset itself, «30 ноября в 23:59 (UTC+3)». The clock counts to the same moment wherever the
 * visitor is; this line tells them whose 23:59 it is.
 */
export function formatDeadline(at: number, offset: number, locale: string): string {
  const wall = new Date(at + offset * 60_000)
  const date = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone: "UTC",
  }).format(wall)
  return `${date} (${offsetLabel(offset)})`
}

/** A number of the clock, two digits at least: 05, 12, 120. */
export const pad = (value: number) => String(value).padStart(2, "0")
